import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { cherrypickImages } from '@go-client/lib/api/image';
import { mapBy, notUndefined, onlyUnique } from '@go-shared/lib/functional';
import { ReservableAddon } from '@go-shared/types/reservable';
import { ObjectId } from 'bson';
import { FormikProps } from 'formik';
import React, { useState, useEffect } from 'react';
import { populate } from '@go-shared/lib/image';
import { Image } from '@go-shared/types/image';
import { BubbleButton, BubbleImageThumbnail, BubbleInput } from './BubbleUI';
import { ReservationFormikForm } from './Formik/ReservationFormik';
import { formatCentsToUSD } from '@go-shared/lib/utils';

export interface ReservationAddonFillerProps {
  formik: FormikProps<ReservationFormikForm>;
  options: ReservableAddon[];
}

export default function ReservationAddonFiller(props: ReservationAddonFillerProps) {
  const { formik, options } = props;

  const { brand } = useServerInfo();
  const [images, setImages] = useState<Image[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);

  async function fetchData() {
    setLoadingImages(true);

    try {
      const images = await cherrypickImages(
        options
          .map(o => o.images)
          .flat()
          .filter(onlyUnique)
          .filter(notUndefined)
      );
      const populatedCherryPickImages = images.map(i => populate(i, brand.imageTypes));
      setImages(populatedCherryPickImages);
    } finally {
      setLoadingImages(false);
    }
  }

  useEffect(() => {
    fetchData().catch(console.error);
  }, []);

  const imagesMap = mapBy(images, i => i.key);

  return (
    <div className="reservation-addon-filler grid grid-cols-3 gap-4">
      {options.map(addon => {
        const existingAddonIdx = formik.values.addons.findIndex(a => a.addonId === addon._id);
        const existingAddon = formik.values.addons[existingAddonIdx];
        const quantity = existingAddon?.quantity ?? 0;

        return (
          <div key={addon._id} className="bg-white p-4 rounded shadow flex flex-col">
            <div className="flex items-start mb-3">
              <BubbleImageThumbnail
                className="w-16 mr-2"
                purpose="plain"
                thumbnail={imagesMap[addon.images[0]]}
                loading={loadingImages}
              />
              <div className="flex-grow grid grid-cols-1">
                <h2 className="font-semibold">{addon.name}</h2>
                <p>
                  {formatCentsToUSD(addon.price)}
                  {addon.pricePerTicket ? ' (per ticket)' : ''}
                </p>
              </div>
            </div>
            <div
              className={`mt-auto flex items-center rounded-lg overflow-hidden border ${existingAddonIdx < 0 ? 'border-navy' : ''}`}
            >
              {existingAddonIdx < 0 ? (
                <>
                  <BubbleButton
                    block
                    variant="white"
                    onClick={() =>
                      formik.setFieldValue('addons', [
                        ...formik.values.addons,
                        {
                          _id: new ObjectId().toHexString(),
                          addonId: addon._id,
                          quantity: 1,
                          _addon: addon,
                        },
                      ])
                    }
                    icon="plus"
                  />
                </>
              ) : (
                <>
                  <BubbleButton
                    icon={quantity > 1 ? 'minus' : 'trash-alt'}
                    variant={quantity > 1 ? 'smoke' : 'crimson'}
                    appearance="squared"
                    onClick={() =>
                      quantity > 1
                        ? formik.setFieldValue(
                            `addons[${existingAddonIdx}].quantity`,
                            Math.max(0, quantity - 1)
                          )
                        : formik.setFieldValue(
                            'addons',
                            formik.values.addons.filter(a => a.addonId !== addon._id)
                          )
                    }
                  />
                  <BubbleInput
                    type="number"
                    block
                    formik={[formik, `addons[${existingAddonIdx}].quantity`]}
                    inputClassName="rounded-0-i"
                    value={quantity}
                    min={0}
                  />
                  <BubbleButton
                    icon="plus"
                    variant="slate"
                    appearance="squared"
                    onClick={() =>
                      formik.setFieldValue(`addons[${existingAddonIdx}].quantity`, quantity + 1)
                    }
                  />
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
